/**
 * Created by Daniel Wang on 09/17/2019.
 * {"UT Code Coverage":{"Reviewed":"12/6/2019","Priority":"Middle","Comments":"Key component, Added UT in main functions and logics.","TargetRate":"60","Author":"Daniel Wang"}}
 * Description:
 * ------ maintenance history ------
 * 07/24/2023   Angela Liang  Added to save the state of Refineby when it is expanded or collapsed.
 * 07/25/2023   Angela Liang  Added control to show and hide Refineby when switching between Gird View and Calendar View.
 */

import { Component, OnInit, ContentChild, TemplateRef, Input, HostBinding, ElementRef, Renderer2, HostListener, SimpleChange, Output, OnDestroy } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { CalendarLayoutActions, CalendarLayoutConfig, LayoutViewType, sidelistView } from './calendar-layout.data';
import { EventEmitter } from 'stream';
import { Subscription } from 'rxjs';

@Component({
    selector: 'calendar-layout',
    templateUrl: './calendar-layout.component.html',
    styleUrls: ['./calendar-layout.component.scss'],
    animations: [trigger('draggingMenu', [transition('1 => 0', [style({
        top: '{{top}}', left: '{{left}}', right: '{{right}}', bottom: '{{bottom}}'
    }
    ), animate('100ms cubic-bezier(0.680, -0.550, 0.265, 1.550)', style('*'))])]), trigger('menuState', [transition(':enter', [style({
        transform: 'scale(0)'
    }
    ), animate('0ms cubic-bezier(0.680, -0.550, 0.265, 1.550)', style({
        transform: 'scale(1)'
    }
    )),
    ]), transition(':leave', [animate('0ms cubic-bezier(0.680, -0.550, 0.265, 1.550)', style({
        transform: 'scale(0)'
    }
    ))])])],
})
export class CalendarLayoutComponent implements OnInit, OnDestroy {
    @ContentChild('refinedBy', { static: false }) refinedBy: TemplateRef<any>;
    @ContentChild('preview', { static: false }) preview: TemplateRef<any>;
    @Input() config: CalendarLayoutConfig;
    /*  below is the variables for layout */
    refineByShow = true;
    sidelistView: LayoutViewType;
    isdraged = false;
    disableCollapse = false;
    /*  above is the variables for layout */

    // for draggingButton animation
    top: string;
    left: string;
    bottom: string;
    right: string;

    private _collapseButton = 28;
    private _expandButton = 28;
    private _navigationWidth = 64;
    private _panelTopMargin = 119;
    private _clickTime: number;
    private _cancelClick = false;
    private _el: HTMLElement;

    // for draggingButton animation
    private _left: number;
    private _top: number;
    private _destroySubscriptions: Array<Subscription> = [];

    // Binding host element to @menuState animation
    @HostBinding('@menuState') public menuState = false;

    constructor(
        private _element: ElementRef,
        private _renderer2: Renderer2,
    ) {
        this._el = this._element.nativeElement;
    }

    @HostListener('window:resize', ['$event'])
    onResize(e) {
        this.initCollapse(e.target.innerWidth);
    }

    ngOnInit() {
        // for being reused without refinedBy
        this.disableCollapse = this.config.disableCollapse;
        this.initCollapse(window.innerWidth);

        // If creating screen, the parameter named isCollapsed will be deleted.
        if (this.config.isCollapsed) {
            this.refineByShow = !this.config.isCollapsed;
        }
        this.initLayout();
        this._destroySubscriptions.push(
            this.config.actions$.subscribe(action => this._onSubscribeActions(action)),
        );
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    collapse() {
        if (!this._cancelClick) {
            this.refineByShow = !this.refineByShow;
            this.config.events$.next({
                type: CalendarLayoutActions.COLLAPSE_CHANGE,
                payload: this.refineByShow
            });

            this.initMaxMin();
        }
        this._cancelClick = false;
    }

    collapsedChange(e) {
        if (this.isdraged) {
            if (e.collapsed) {
                this.sidelistView.previewWidth = e.collapsedComponentSize + e.sizes[e.sizes.length - 1] + 0.01;
            } else {
                this.sidelistView.previewWidth = e.sizes[e.sizes.length - 1] - e.collapsedComponentSize - 0.01;
            }
        } else {
            this.initWidth();
        }
    }

    /* below is the function for layout */
    initCollapse(width: number) {
        if (width >= 1024) {
            this.refineByShow = true && !this.disableCollapse;
        } else {
            this.refineByShow = false;
        }
    }

    initLayout() {
        this.sidelistView = Object.assign({}, sidelistView);
        this.initWidth();
        this.initMaxMin();
    }

    initWidth() {
        if (this.refineByShow) {
            this.sidelistView.previewWidth = sidelistView.previewWidth;
        } else {
            this.sidelistView.previewWidth = sidelistView.previewMax;
        }
    }

    initMaxMin() {
        // if expand, set the max and min without collapse
        // if collapsed, set the max and min with collapse
        if (this.refineByShow) {
            this.sidelistView.previewMax = sidelistView.previewMax;
            this.sidelistView.previewMin = sidelistView.previewMin;
        } else {
            this.sidelistView.previewMax = sidelistView.previewMax;
            this.sidelistView.previewMin = sidelistView.previewMin;
        }
    }

    sizeChange(e) {
        this.isdraged = true;
    }
    /* above is the function for layout */

    // button start move
    public onPanStart(): void {
    }

    // button move end
    public onPanEnd(): void {
        this._renderer2.setStyle(this._el.querySelector('.collapse'), 'top', this.top);
        // need to set left to auto, otherwise the left value(moving) will affect
        this._renderer2.setStyle(this._el.querySelector('.collapse'), 'left', 'auto');
        this._renderer2.setStyle(this._el.querySelector('.collapse'), 'right', '-1px');
        this._renderer2.setStyle(this._el.querySelector('.expand'), 'top', this.top);
        this._renderer2.setStyle(this._el.querySelector('.expand'), 'left', 0);
    }

    // collapse button is moving
    public onBottonMove(event: any): void {
        const y = event.center.y;
        const x = event.center.x;
        // need to minus the panel top margin, because the icon will locate according to the panel parent, not the window
        this._top = y - this._panelTopMargin;
        this.top = this._top + 'px';
        this._left = x - this._collapseButton / 2 - this._navigationWidth;
        this.left = this._left + 'px';
        this._renderer2.setStyle(this._el.querySelector('.collapse'), 'top', this.top);
        this._renderer2.setStyle(this._el.querySelector('.collapse'), 'left', this.left);
        // if we want to move button, the button will not do the click operation
        this._cancelClick = true;
    }

    // expand button is moving
    public onBottonMoveForExpand(event: any): void {
        const y = event.center.y;
        const x = event.center.x;
        // need to minus the panel top margin, because the icon will locate according to the panel parent, not the window
        this._top = y - this._panelTopMargin;
        this.top = this._top + 'px';
        this._left = x - this._expandButton / 2 - this._navigationWidth;
        this.left = this._left + 'px';
        this._renderer2.setStyle(this._el.querySelector('.expand'), 'top', this.top);
        this._renderer2.setStyle(this._el.querySelector('.expand'), 'left', this.left);
        this._cancelClick = true;
    }
    private _onSubscribeActions(action) {
        if (action.type === CalendarLayoutActions.LAYOUT_CHANGE) {
            this.disableCollapse = this.config.disableCollapse;
            if (this.disableCollapse) {
                this.refineByShow = false;
            } else {
                this.refineByShow = !this.config.isCollapsed;
            }
        }
    }
}
